<template>
  <div class="app-container discussion-admin-page">
    <div class="page-heading">
      <div>
        <h2>交流討論管理</h2>
        <p>查看內容、隱藏或復原文章，並處理會員檢舉。</p>
      </div>
      <el-button icon="el-icon-refresh" :loading="loading" @click="reload">重新載入</el-button>
    </div>

    <el-alert
      title="本期僅 ITA-DEV 與 ITA-ADMIN 可使用；未來若開放旅行社，將由集中式權限政策調整。"
      type="info"
      :closable="false"
      show-icon
      class="permission-alert"
    />

    <el-alert
      v-if="featureDisabled"
      type="warning"
      title="專區已關閉"
      description="領隊導遊專區目前已關閉，隱藏、復原與檢舉處理等寫操作已停用；如需開放，請聯絡系統管理員。"
      :closable="false"
      show-icon
      class="permission-alert"
    />

    <el-tabs v-model="activeTab" @tab-click="reload">
      <el-tab-pane label="文章與留言" name="posts">
        <el-form :inline="true" :model="postQuery" @submit.native.prevent>
          <el-form-item label="關鍵字">
            <el-input
              v-model.trim="postQuery.keyword"
              maxlength="100"
              clearable
              @keyup.enter.native="searchPosts"
            />
          </el-form-item>
          <el-form-item label="顯示狀態">
            <el-select v-model="postQuery.visibility" clearable placeholder="全部">
              <el-option label="公開" value="visible" />
              <el-option label="已隱藏" value="hidden" />
              <el-option label="草稿" value="draft" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" :loading="loading" @click="searchPosts"
              >查詢</el-button
            >
          </el-form-item>
        </el-form>

        <el-alert v-if="loadError" type="error" :title="loadError" :closable="false" show-icon />
        <el-table v-loading="loading" :data="posts" border stripe empty-text="尚無討論文章">
          <el-table-column prop="title" label="標題" min-width="240" show-overflow-tooltip />
          <el-table-column prop="authorName" label="作者" min-width="120" />
          <el-table-column prop="category" label="分類" min-width="120" />
          <el-table-column prop="commentCount" label="留言" width="80" align="center" />
          <el-table-column prop="reportCount" label="檢舉" width="80" align="center" />
          <el-table-column
            prop="hiddenReason"
            label="隱藏原因"
            min-width="160"
            show-overflow-tooltip
          />
          <el-table-column prop="updatedAt" label="更新時間" width="170" />
          <el-table-column label="狀態" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="postStatusType(scope.row)">
                {{ postStatusLabel(scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="270" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" @click="openPost(scope.row)">查看</el-button>
              <el-button size="mini" @click="openComments(scope.row)">留言</el-button>
              <el-button
                size="mini"
                :type="scope.row.hidden ? 'success' : 'warning'"
                :disabled="featureDisabled"
                @click="changeVisibility(scope.row)"
              >
                {{ scope.row.hidden ? '復原' : '隱藏' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <pagination
          v-show="postTotal > 0"
          :total="postTotal"
          :page.sync="postQuery.page"
          :limit.sync="postQuery.limit"
          @pagination="fetchPosts"
        />
      </el-tab-pane>

      <el-tab-pane label="檢舉處理" name="reports">
        <el-form :inline="true" :model="reportQuery">
          <el-form-item label="處理狀態">
            <el-select
              v-model="reportQuery.status"
              clearable
              placeholder="全部"
              @change="searchReports"
            >
              <el-option label="待處理" value="pending" />
              <el-option label="已處理" value="resolved" />
              <el-option label="不成立" value="dismissed" />
            </el-select>
          </el-form-item>
        </el-form>
        <el-alert v-if="loadError" type="error" :title="loadError" :closable="false" show-icon />
        <el-table v-loading="loading" :data="reports" border stripe empty-text="尚無檢舉資料">
          <el-table-column label="對象" width="100">
            <template slot-scope="scope">{{ targetTypeLabel(scope.row.targetType) }}</template>
          </el-table-column>
          <el-table-column
            prop="targetSummary"
            label="內容摘要"
            min-width="230"
            show-overflow-tooltip
          />
          <el-table-column prop="reason" label="檢舉原因" min-width="180" show-overflow-tooltip />
          <el-table-column
            prop="resolutionNote"
            label="處理說明"
            min-width="180"
            show-overflow-tooltip
          />
          <el-table-column prop="reportedAt" label="檢舉時間" width="170" />
          <el-table-column label="狀態" width="100">
            <template slot-scope="scope">{{ reportStatusLabel(scope.row.status) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="180" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                v-if="String(scope.row.status).toLowerCase() === 'pending'"
                size="mini"
                type="danger"
                :disabled="featureDisabled"
                @click="resolveReport(scope.row, 'resolved')"
                >成立</el-button
              >
              <el-button
                v-if="String(scope.row.status).toLowerCase() === 'pending'"
                size="mini"
                :disabled="featureDisabled"
                @click="resolveReport(scope.row, 'dismissed')"
                >不成立</el-button
              >
              <span v-else>已完成</span>
            </template>
          </el-table-column>
        </el-table>
        <pagination
          v-show="reportTotal > 0"
          :total="reportTotal"
          :page.sync="reportQuery.page"
          :limit.sync="reportQuery.limit"
          @pagination="fetchReports"
        />
      </el-tab-pane>
    </el-tabs>

    <el-dialog title="文章內容" :visible.sync="postDialog.visible" width="760px">
      <el-descriptions v-if="postDialog.post" :column="2" border>
        <el-descriptions-item label="標題" :span="2">{{
          postDialog.post.title
        }}</el-descriptions-item>
        <el-descriptions-item label="作者">{{ postDialog.post.authorName }}</el-descriptions-item>
        <el-descriptions-item label="狀態">
          <el-tag size="mini" :type="postStatusType(postDialog.post)">
            {{ postStatusLabel(postDialog.post) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="分類">{{
          postDialog.post.category || '無'
        }}</el-descriptions-item>
        <el-descriptions-item label="地區">{{
          postDialog.post.region || '無'
        }}</el-descriptions-item>
        <el-descriptions-item label="標籤" :span="2">
          {{ (postDialog.post.tags || []).join('、') || '無' }}
        </el-descriptions-item>
        <el-descriptions-item label="參考連結" :span="2">
          {{ postDialog.post.referenceUrl || '無' }}
        </el-descriptions-item>
        <el-descriptions-item label="內容" :span="2">
          <div class="post-content">{{ postDialog.post.content }}</div>
        </el-descriptions-item>
        <el-descriptions-item v-if="postDialog.post.hidden" label="隱藏原因" :span="2">
          {{ postDialog.post.hiddenReason }}
        </el-descriptions-item>
        <el-descriptions-item label="互動" :span="2">
          瀏覽 {{ postDialog.post.viewCount || 0 }}／按讚 {{ postDialog.post.likeCount || 0 }}／收藏
          {{ postDialog.post.bookmarkCount || 0 }}／留言
          {{ postDialog.post.commentCount || 0 }}／檢舉
          {{ postDialog.post.reportCount || 0 }}
        </el-descriptions-item>
        <el-descriptions-item label="建立時間">{{
          postDialog.post.createdAt
        }}</el-descriptions-item>
        <el-descriptions-item label="更新時間">{{
          postDialog.post.updatedAt
        }}</el-descriptions-item>
      </el-descriptions>
      <span v-if="postDialog.post" slot="footer">
        <el-button
          :type="postDialog.post.hidden ? 'success' : 'warning'"
          :disabled="featureDisabled"
          @click="changeVisibility(postDialog.post)"
        >
          {{ postDialog.post.hidden ? '復原' : '隱藏' }}
        </el-button>
        <el-button @click="postDialog.visible = false">關閉</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="commentDialog.post ? `留言管理｜${commentDialog.post.title}` : '留言管理'"
      :visible.sync="commentDialog.visible"
      width="760px"
    >
      <el-alert
        v-if="commentDialog.error"
        type="error"
        :title="commentDialog.error"
        :closable="false"
        show-icon
      />
      <el-table
        v-loading="commentDialog.loading"
        :data="commentDialog.items"
        border
        stripe
        empty-text="尚無留言"
      >
        <el-table-column prop="authorName" label="作者" width="130" />
        <el-table-column prop="content" label="留言內容" min-width="300" show-overflow-tooltip />
        <el-table-column
          prop="hiddenReason"
          label="隱藏原因"
          min-width="160"
          show-overflow-tooltip
        />
        <el-table-column prop="createdAt" label="留言時間" width="170" />
        <el-table-column label="狀態" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.hidden ? 'danger' : 'success'">
              {{ scope.row.hidden ? '已隱藏' : '公開' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" align="center">
          <template slot-scope="scope">
            <el-button
              size="mini"
              :type="scope.row.hidden ? 'success' : 'warning'"
              :disabled="featureDisabled"
              @click="changeCommentVisibility(scope.row)"
            >
              {{ scope.row.hidden ? '復原' : '隱藏' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <pagination
        v-show="commentDialog.total > 0"
        :total="commentDialog.total"
        :page.sync="commentDialog.query.page"
        :limit.sync="commentDialog.query.limit"
        @pagination="fetchComments"
      />
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import {
  getDiscussionComments,
  getDiscussionReports,
  getDiscussions,
  resolveDiscussionReport,
  updateDiscussionCommentVisibility,
  updateDiscussionVisibility,
} from '@/api/tourLeaderAdmin'
import { requireSuccessfulResponse, toPagedResult } from '@/utils/tourLeaderApi'
import { isTourLeaderFeatureDisabled } from '@/utils/tourLeaderPhaseSeven'
import { hasAnyRole, TOUR_LEADER_DISCUSSION_ROLES } from '@/utils/tourLeaderPermissions'

export default {
  name: 'TourLeaderDiscussionIndex',
  components: { Pagination },
  data() {
    return {
      activeTab: 'posts',
      loading: false,
      loadError: '',
      featureDisabled: false,
      posts: [],
      postTotal: 0,
      postQuery: { keyword: '', visibility: '', page: 1, limit: 20 },
      reports: [],
      reportTotal: 0,
      reportQuery: { status: 'pending', page: 1, limit: 20 },
      postDialog: { visible: false, post: null },
      commentDialog: {
        visible: false,
        loading: false,
        error: '',
        post: null,
        items: [],
        total: 0,
        query: { page: 1, limit: 20 },
      },
    }
  },
  created() {
    if (!hasAnyRole(this.$store.getters.roles, TOUR_LEADER_DISCUSSION_ROLES)) {
      this.$router.replace('/401')
      return
    }
    this.fetchPosts()
  },
  methods: {
    targetTypeLabel(value) {
      return String(value).toLowerCase() === 'comment' ? '留言' : '文章'
    },
    postStatusLabel(post) {
      if (post.hidden) return '已隱藏'
      return String(post.status).toLowerCase() === 'draft' ? '草稿' : '公開'
    },
    postStatusType(post) {
      if (post.hidden) return 'danger'
      return String(post.status).toLowerCase() === 'draft' ? 'info' : 'success'
    },
    reportStatusLabel(value) {
      return (
        {
          pending: '待處理',
          resolved: '已處理',
          dismissed: '不成立',
        }[String(value).toLowerCase()] || value
      )
    },
    reload() {
      if (this.activeTab === 'reports') {
        this.fetchReports()
      } else {
        this.fetchPosts()
      }
    },
    searchPosts() {
      this.postQuery.page = 1
      this.fetchPosts()
    },
    searchReports() {
      this.reportQuery.page = 1
      this.fetchReports()
    },
    async fetchPosts() {
      this.loading = true
      this.loadError = ''
      try {
        const result = toPagedResult(await getDiscussions(this.postQuery))
        this.posts = result.items
        this.postTotal = result.total
        this.featureDisabled = false
      } catch (error) {
        this.posts = []
        this.postTotal = 0
        if (!this.handleFeatureDisabled(error)) {
          this.loadError = '無法載入討論內容。'
        }
      } finally {
        this.loading = false
      }
    },
    async fetchReports() {
      this.loading = true
      this.loadError = ''
      try {
        const result = toPagedResult(await getDiscussionReports(this.reportQuery))
        this.reports = result.items
        this.reportTotal = result.total
        this.featureDisabled = false
      } catch (error) {
        this.reports = []
        this.reportTotal = 0
        if (!this.handleFeatureDisabled(error)) {
          this.loadError = '無法載入檢舉資料。'
        }
      } finally {
        this.loading = false
      }
    },
    handleFeatureDisabled(error) {
      if (!isTourLeaderFeatureDisabled(error)) return false
      this.featureDisabled = true
      this.posts = []
      this.postTotal = 0
      this.reports = []
      this.reportTotal = 0
      this.loadError = ''
      this.loading = false
      this.postDialog.visible = false
      this.commentDialog.visible = false
      return true
    },
    async changeVisibility(post) {
      if (this.featureDisabled) return false
      const hidden = !post.hidden
      try {
        const prompt = await this.$prompt(`請輸入${hidden ? '隱藏' : '復原'}原因`, '確認操作', {
          inputValidator: value => Boolean(value && value.trim()),
          inputErrorMessage: '請輸入原因',
        })
        const updated = requireSuccessfulResponse(
          await updateDiscussionVisibility(post.id, hidden, prompt.value.trim())
        )
        if (this.postDialog.post && this.postDialog.post.id === post.id) {
          this.postDialog.post = { ...this.postDialog.post, ...updated }
        }
        this.$message.success('內容狀態已更新')
        await this.fetchPosts()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('內容狀態更新失敗。')
        }
      }
    },
    openPost(post) {
      this.postDialog.post = post
      this.postDialog.visible = true
    },
    openComments(post) {
      this.commentDialog.post = post
      this.commentDialog.visible = true
      this.commentDialog.query.page = 1
      this.fetchComments()
    },
    async fetchComments() {
      if (!this.commentDialog.post) return
      this.commentDialog.loading = true
      this.commentDialog.error = ''
      try {
        const result = toPagedResult(
          await getDiscussionComments(this.commentDialog.post.id, this.commentDialog.query)
        )
        this.commentDialog.items = result.items
        this.commentDialog.total = result.total
      } catch (error) {
        this.commentDialog.items = []
        this.commentDialog.total = 0
        this.commentDialog.error = '無法載入留言資料。'
      } finally {
        this.commentDialog.loading = false
      }
    },
    async changeCommentVisibility(comment) {
      if (this.featureDisabled) return false
      const hidden = !comment.hidden
      try {
        const prompt = await this.$prompt(`請輸入${hidden ? '隱藏' : '復原'}原因`, '確認操作', {
          inputValidator: value => Boolean(value && value.trim()),
          inputErrorMessage: '請輸入原因',
        })
        requireSuccessfulResponse(
          await updateDiscussionCommentVisibility(comment.id, hidden, prompt.value.trim())
        )
        this.$message.success('留言狀態已更新')
        await Promise.all([this.fetchComments(), this.fetchPosts()])
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('留言狀態更新失敗。')
        }
      }
    },
    async resolveReport(report, status) {
      if (this.featureDisabled) return false
      try {
        const prompt = await this.$prompt('請輸入處理說明', '處理檢舉', {
          inputValidator: value => Boolean(value && value.trim()),
          inputErrorMessage: '請輸入處理說明',
        })
        requireSuccessfulResponse(
          await resolveDiscussionReport(report.id, { status, note: prompt.value.trim() })
        )
        this.$message.success('檢舉已處理')
        await this.fetchReports()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('檢舉處理失敗。')
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.discussion-admin-page {
  .page-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    h2 {
      margin: 0 0 6px;
    }

    p {
      margin: 0;
      color: #606266;
    }
  }

  .permission-alert {
    margin-bottom: 16px;
  }

  .post-content {
    max-height: 360px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style>
